import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videos, categories } from '@/mocks/videos';
import VideoCard from '@/components/feature/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'newest' | 'views'>('rating');

  const category = categories.find((c) => c.slug === slug);
  const categoryVideos = videos.filter((v) => v.category === slug);

  const sortedVideos = [...categoryVideos].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'newest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return b.views - a.views;
  });

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category?.label || slug || '' },
  ];

  return (
    <main>
      <Breadcrumb items={breadcrumbItems} />

      {/* Category Header */}
      <section className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {category?.label || '视频分类'}
              </h1>
              <p className="text-foreground-600 text-sm">
                {category?.desc || '浏览日韩视频内容'} · 共 {categoryVideos.length} 部视频
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSortBy('rating')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  sortBy === 'rating' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                评分最高
              </button>
              <button
                onClick={() => setSortBy('newest')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  sortBy === 'newest' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                最新更新
              </button>
              <button
                onClick={() => setSortBy('views')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  sortBy === 'views' ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                最多观看
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full px-4 md:px-6 pb-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-accent-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-sm w-4 h-4 inline-flex items-center justify-center`}></i>
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Video Grid */}
      <section className="w-full px-4 md:px-6 py-8 bg-background-100">
        <div className="max-w-[1400px] mx-auto">
          {sortedVideos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sortedVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-video-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无日韩视频内容</p>
              <Link
                to="/"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Related Categories */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            其他日韩视频分类
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-4 p-5 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm">{cat.label}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{cat.desc}</p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-400 ml-auto w-5 h-5 flex items-center justify-center"></i>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}